import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Wrench,
  ChevronDown,
  ChevronRight,
  Loader2,
  Check,
  XCircle,
  Copy,
} from 'lucide-react';
import { useToolDisplayName } from '../../hooks/useTools';
import { formatElapsed } from '../../utils/format';
import { fetchOffloadedContent } from '../../utils/s3-offload';
import { getToolIcon } from '../../utils/tool-icons';
import { getToolDetail } from '../../utils/tool-details';
import { JsonContent } from '../ui/json-content';
import { McpAuthCallout } from './McpAuthCallout';
import type { McpAuthRequiredResult } from '../../types/mcp';

interface ToolCallCardProps {
  toolName: string;
  /** Human-readable display name from the backend (preferred over context lookup). */
  toolDisplayName?: string;
  toolCallId: string;
  args: Record<string, unknown>;
  result?: unknown;
  isError?: boolean;
  status?: { type: string };
  /** Backend-provided tool duration in milliseconds */
  durationMs?: number;
  /** Compact mode for inline/chain-of-thought rendering */
  compact?: boolean;
  /** S3 presigned URL for offloaded large output */
  outputUrl?: string;
  /** Optional trace time element rendered in the compact header row */
  traceTime?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const MAX_STRING_LEN = 2000;

/**
 * Walk a JSON-compatible value and truncate any string leaf longer than
 * `maxLen` characters, appending "…". Arrays longer than 20 items are trimmed.
 */
function truncateDeep(value: unknown, maxLen = MAX_STRING_LEN): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    return value.length > maxLen ? value.slice(0, maxLen) + '…' : value;
  }
  if (Array.isArray(value)) {
    const trimmed = value.slice(0, 20).map((v) => truncateDeep(v, maxLen));
    if (value.length > 20) trimmed.push(`… ${value.length - 20} more items`);
    return trimmed;
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = truncateDeep(v, maxLen);
    }
    return out;
  }
  return value;
}

/**
 * Best-effort JSON formatter that works on truncated / invalid JSON strings.
 * Adds newlines and indentation by tracking braces, brackets, and strings.
 */
function bestEffortJsonFormat(raw: string): string {
  if (!/^\s*[{[]/.test(raw)) return raw;

  let indent = 0;
  let out = '';
  let inString = false;
  let escaped = false;

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];

    if (escaped) {
      out += ch;
      escaped = false;
      continue;
    }
    if (ch === '\\' && inString) {
      out += ch;
      escaped = true;
      continue;
    }
    if (ch === '"') {
      out += ch;
      inString = !inString;
      continue;
    }
    if (inString) {
      out += ch;
      continue;
    }

    switch (ch) {
      case '{':
      case '[':
        indent++;
        out += ch + '\n' + '  '.repeat(indent);
        break;
      case '}':
      case ']':
        indent = Math.max(0, indent - 1);
        out += '\n' + '  '.repeat(indent) + ch;
        break;
      case ',':
        out += ch + '\n' + '  '.repeat(indent);
        break;
      case ':':
        out += ': ';
        break;
      default:
        out += ch;
    }
  }

  return out;
}

/**
 * Normalise an unknown value into a pretty-printed string.
 *
 * 1. If it's a string, try JSON.parse → truncate → pretty-print.
 *    If parsing fails (truncated JSON from backend), use best-effort formatter.
 * 2. If it's an object/array, truncate leaves then pretty-print.
 */
function formatValue(value: unknown): string {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(truncateDeep(parsed), null, 2);
    } catch {
      // Truncated or invalid JSON — best-effort formatting
      return bestEffortJsonFormat(value);
    }
  }
  return JSON.stringify(truncateDeep(value), null, 2);
}

/**
 * Detect the shape an MCP tool returns when the caller hasn't completed
 * OAuth consent yet. We accept either a plain object or a JSON-encoded
 * string so it doesn't matter whether the agent unwrapped the JSON.
 */
function detectMcpAuthShape(value: unknown): McpAuthRequiredResult | null {
  const parsed: unknown =
    typeof value === 'string'
      ? (() => {
          try {
            return JSON.parse(value);
          } catch {
            return null;
          }
        })()
      : value;
  if (!parsed || typeof parsed !== 'object') return null;
  const candidate = parsed as Record<string, unknown>;
  if (candidate.requiresAuth !== true) return null;
  if (typeof candidate.serverId !== 'string') return null;
  if (typeof candidate.serverName !== 'string') return null;
  if (typeof candidate.authorizationUrl !== 'string') return null;
  return {
    requiresAuth: true,
    serverId: candidate.serverId,
    serverName: candidate.serverName,
    authorizationUrl: candidate.authorizationUrl,
    hint: typeof candidate.hint === 'string' ? candidate.hint : undefined,
  };
}

/** Raw pretty-print for clipboard — no truncation, no best-effort reformatting. */
function rawValue(value: unknown): string {
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
}

// ---------------------------------------------------------------------------
// Copy action bar (shared between compact and full-size expanded views)
// ---------------------------------------------------------------------------

function CopyBar({
  copied,
  onCopy,
  compact,
}: {
  copied: boolean;
  onCopy: () => void;
  compact?: boolean;
}) {
  return (
    <div className={`flex items-center ${compact ? 'mt-1' : 'border-t border-border pt-2'}`}>
      <button
        type="button"
        className="flex items-center gap-1 rounded p-0.5 text-muted-foreground/60 hover:text-foreground"
        onClick={onCopy}
        title="Copy tool call summary"
      >
        {copied ? (
          <Check className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        ) : (
          <Copy className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Elapsed time cache — survives unmount/remount (accordion collapse/expand).
// Bounded to prevent slow memory leak in long sessions.
// ---------------------------------------------------------------------------

const ELAPSED_CACHE_MAX = 500;
const elapsedCache = new Map<string, number>();

function cacheElapsed(toolCallId: string, value: number) {
  elapsedCache.set(toolCallId, value);
  // Evict oldest entries when cache exceeds bound
  if (elapsedCache.size > ELAPSED_CACHE_MAX) {
    const firstKey = elapsedCache.keys().next().value;
    if (firstKey !== undefined) elapsedCache.delete(firstKey);
  }
}

// ---------------------------------------------------------------------------
// ToolCallCard
// ---------------------------------------------------------------------------

export function ToolCallCard({
  toolName,
  toolDisplayName: backendDisplayName,
  toolCallId,
  args,
  result,
  isError,
  status,
  durationMs,
  compact,
  outputUrl,
  traceTime,
}: ToolCallCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [offloadedResult, setOffloadedResult] = useState<unknown>(undefined);
  const [offloadLoading, setOffloadLoading] = useState(false);
  const [offloadError, setOffloadError] = useState<string | null>(null);
  const displayName = useToolDisplayName(toolName, backendDisplayName);

  // A tool is "running" when we have no result yet. We can't rely solely on
  // status.type because chain-of-thought tool-call parts don't carry a status
  // field — only makeAssistantToolUI provides one.
  const isRunning = result === undefined && !outputUrl && status?.type !== 'complete' && status?.type !== 'incomplete';
  const isDone = result !== undefined || outputUrl !== undefined;
  const hasError = !!isError;

  // Elapsed timer — starts on mount while running, freezes when result arrives.
  // Uses a module-level cache so the value survives accordion collapse/expand.
  const cached = elapsedCache.get(toolCallId);
  const startRef = useRef(Date.now());
  const frozenRef = useRef<number | null>(cached ?? null);
  const [elapsed, setElapsed] = useState(cached ?? 0);

  useEffect(() => {
    if (!isRunning) {
      if (frozenRef.current === null) {
        frozenRef.current = Math.round((Date.now() - startRef.current) / 1000);
        setElapsed(frozenRef.current);
      }
      cacheElapsed(toolCallId, frozenRef.current);
      return;
    }
    const timer = setInterval(() => {
      setElapsed(Math.round((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [isRunning, toolCallId]);

  const backendElapsed = typeof durationMs === 'number' && Number.isFinite(durationMs) ? Math.round(durationMs / 1000) : null;
  const displayElapsed = backendElapsed ?? (frozenRef.current ?? elapsed);

  // Lazy-fetch offloaded output when expanded
  useEffect(() => {
    if (!expanded || !outputUrl || offloadedResult !== undefined || offloadLoading) return;
    setOffloadLoading(true);
    setOffloadError(null);
    fetchOffloadedContent(outputUrl)
      .then(setOffloadedResult)
      .catch((e) => setOffloadError(e.message))
      .finally(() => setOffloadLoading(false));
  }, [expanded, outputUrl, offloadedResult, offloadLoading]);

  // Use offloaded result if available, otherwise use inline result
  const effectiveResult = offloadedResult !== undefined ? offloadedResult : result;

  // Memoize expensive formatValue calls (JSON.parse + truncateDeep + JSON.stringify)
  const formattedArgs = useMemo(() => formatValue(args), [args]);
  const formattedResult = useMemo(() => effectiveResult !== undefined ? formatValue(effectiveResult) : undefined, [effectiveResult]);
  // Structured "needs OAuth consent" tool-result — when present we render an
  // inline CTA in place of the raw JSON pane. Detection runs on the same
  // value as formatting so copy-to-clipboard still gets the raw payload.
  const mcpAuthDetail = useMemo(
    () => (effectiveResult !== undefined ? detectMcpAuthShape(effectiveResult) : null),
    [effectiveResult],
  );

  const handleCopy = useCallback(() => {
    const text = [
      `Tool: ${displayName}`,
      `ID: ${toolCallId}`,
      `Input: ${rawValue(args)}`,
      isDone
        ? `${hasError ? 'Error' : 'Output'}: ${rawValue(effectiveResult)}`
        : null,
    ]
      .filter(Boolean)
      .join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [displayName, toolCallId, args, effectiveResult, isDone, hasError]);

  if (compact) {
    return (
      <div className="text-sm">
        <div
          className="flex w-full cursor-pointer items-center gap-1.5 text-left text-muted-foreground"
          onClick={() => setExpanded(!expanded)}
          role="button"
        >
          {(() => { const Icon = getToolIcon(toolName); return <Icon className="h-3.5 w-3.5 shrink-0 text-accent-tool-fg/70" />; })()}
          <span className="min-w-0 truncate text-sm">
            {/* A full phrase from getToolDetail ("Listing runs", "Retrieving
                step 5", ...) replaces the display name entirely so the row
                reads as a sentence. Falls back to the raw display name when
                no phrase can be derived. */}
            {getToolDetail(toolName, args) ?? displayName}
          </span>
          {displayElapsed > 0 && (
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground/60">
              ({formatElapsed(displayElapsed)})
            </span>
          )}

          <span className="ml-auto flex shrink-0 items-center gap-1.5">
            {isRunning && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            )}
            {isDone && !hasError && (
              <Check className="h-3 w-3 text-green-500" />
            )}
            {hasError && (
              <XCircle className="h-3 w-3 text-red-500" />
            )}
            {traceTime}
          </span>
        </div>

        {expanded && (
          <div className="ml-4 mt-1 space-y-1.5">
            <div>
              <div className="text-[10px] font-medium uppercase text-muted-foreground/60 mb-0.5">
                Input
              </div>
              <JsonContent maxHeight="16rem" showHeader={false}>{JSON.stringify(args)}</JsonContent>
            </div>

            {(effectiveResult !== undefined || offloadLoading || offloadError) && (
              <div>
                <div
                  className={`text-[10px] font-medium uppercase mb-0.5 ${hasError ? 'text-red-500' : 'text-muted-foreground/60'}`}
                >
                  {hasError ? 'Error' : 'Output'}
                </div>
                {offloadLoading && (
                  <div className="flex items-center gap-1 py-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                  </div>
                )}
                {offloadError && (
                  <div className="text-xs text-red-500 py-1">Failed to load: {offloadError}</div>
                )}
                {mcpAuthDetail ? (
                  <McpAuthCallout detail={mcpAuthDetail} compact />
                ) : (
                  formattedResult !== undefined && (
                    hasError ? (
                      <div className="rounded bg-status-error-bg p-2 text-xs text-status-error-fg whitespace-pre-wrap break-words max-h-60 overflow-auto">
                        {formattedResult}
                      </div>
                    ) : (
                      <JsonContent maxHeight="16rem" showHeader={false}>{typeof effectiveResult === 'string' ? effectiveResult : JSON.stringify(effectiveResult)}</JsonContent>
                    )
                  )
                )}
              </div>
            )}

            <CopyBar copied={copied} onCopy={handleCopy} compact />
          </div>
        )}
      </div>
    );
  }

  // Full-size card (used in Thread tool UI)
  return (
    <div className="my-2 rounded-md border border-border bg-muted text-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left font-medium text-foreground hover:bg-muted/80"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        <Wrench className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate font-mono text-xs">
          {displayName}
        </span>
        {displayElapsed > 0 && (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground/60">
            ({formatElapsed(displayElapsed)})
          </span>
        )}

        <span className="ml-auto flex shrink-0 items-center">
          {isRunning && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {isDone && !hasError && (
            <Check className="h-4 w-4 text-green-500" />
          )}
          {hasError && (
            <XCircle className="h-4 w-4 text-red-500" />
          )}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-border px-3 py-2 space-y-2">
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">
              Input
            </div>
            <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-card p-2 font-mono text-xs text-foreground border border-border">
              {formattedArgs}
            </pre>
          </div>

          {(effectiveResult !== undefined || offloadLoading || offloadError) && (
            <div>
              <div
                className={`text-xs font-medium mb-1 ${hasError ? 'text-red-600' : 'text-muted-foreground'}`}
              >
                {hasError ? 'Error' : 'Output'}
              </div>
              {offloadLoading && (
                <div className="flex items-center gap-1 py-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                </div>
              )}
              {offloadError && (
                <div className="text-xs text-red-500 py-1">Failed to load: {offloadError}</div>
              )}
              {mcpAuthDetail ? (
                <McpAuthCallout detail={mcpAuthDetail} />
              ) : (
                <pre
                  className={`max-h-60 overflow-auto whitespace-pre-wrap break-words rounded p-2 font-mono text-xs border ${
                    hasError
                      ? 'bg-status-error-bg text-status-error-fg border-status-error-border'
                      : 'bg-card text-foreground border-border'
                  }`}
                >
                  {formattedResult}
                </pre>
              )}
            </div>
          )}

          <CopyBar copied={copied} onCopy={handleCopy} />
        </div>
      )}
    </div>
  );
}
